import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetEffortByIdQuery } from 'store/api/efforts';
import { EffortForm } from 'types/efforts';
import { useClassifiers } from 'utils/useClassifiers';
import { EFFORT_FORM_CLASSIFIERS, effortDtoToForm } from './effortFormMapping';
import EffortPageForm from './EffortPageForm';

const ClassicEffortPageEdit = (): JSX.Element => {
    const { id: effortId } = useParams();

    const { data: effort, isLoading, isFetching, isError, refetch } = useGetEffortByIdQuery(effortId ?? skipToken);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EFFORT_FORM_CLASSIFIERS });

    const initialData = useMemo<EffortForm | undefined>(
        () => (effort ? effortDtoToForm(effort, classifiers) : undefined),
        [effort, classifiers]
    );

    if (isLoading || isFetching || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return (
        <EffortPageForm
            type="edit"
            expenses={effort?.expenses || []}
            classifiers={classifiers}
            initialData={initialData}
            refetch={() => {
                void refetch();
            }}
            fallbackUrl={effortId ? `/effort/${effortId}` : '/efforts'}
            isDeletable={effort?.isDeletable ?? false}
        />
    );
};

/** In the new design an effort is edited section by section on its page, so its edit URL opens Overview there. */
const EffortPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: effortId } = useParams();
    return isNew ? <Navigate to={`/effort/${effortId}?edit=overview`} replace /> : <ClassicEffortPageEdit />;
};

export default EffortPageEdit;
